import { useState, useEffect } from "react";
import axios from "axios";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import { Mail, CheckCircle, XCircle, RefreshCw, Trash2 } from "lucide-react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const EmailLogs = () => {
  const [emailLogs, setEmailLogs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [emailLogsStats, setEmailLogsStats] = useState({ total: 0, delivered: 0, failed: 0, by_type: {} });
  const [filter, setFilter] = useState({ status: '', email_type: '' });

  useEffect(() => {
    fetchEmailLogs();
    fetchEmailLogsStats();
  }, [filter]);

  const fetchEmailLogs = async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem('token');
      const params = new URLSearchParams();
      params.append('limit', '100');
      if (filter.status) params.append('status', filter.status);
      if (filter.email_type) params.append('email_type', filter.email_type);
      
      const response = await axios.get(`${API}/email-logs?${params.toString()}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setEmailLogs(response.data.logs || []);
    } catch (error) {
      console.error("Failed to fetch email logs");
      toast.error("Failed to fetch email logs");
    }
    setLoading(false);
  };

  const fetchEmailLogsStats = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API}/email-logs/stats`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setEmailLogsStats(response.data);
    } catch (error) {
      console.error("Failed to fetch email stats");
    }
  };

  const handleClearEmailLogs = async () => {
    if (!window.confirm('Are you sure you want to clear ALL email logs? This action cannot be undone.')) {
      return;
    }
    
    try {
      const token = localStorage.getItem('token');
      await axios.delete(`${API}/email-logs`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setEmailLogs([]);
      setEmailLogsStats({ total: 0, delivered: 0, failed: 0, by_type: {} });
      toast.success("Email logs cleared successfully");
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to clear email logs");
    }
  };

  const formatEmailType = (type) => {
    if (!type) return 'Unknown';
    return type.replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase());
  };

  return (
    <div data-testid="email-logs-page">
      <div className="mb-6">
        <h1 className="text-3xl font-bold" style={{ color: '#FFFFFF' }}>Email Delivery Logs</h1>
        <p style={{ color: '#B0B0B0' }}>Track all emails sent by the system</p>
      </div>

      <Card style={{ background: 'rgba(30, 42, 74, 0.6)', border: '1px solid rgba(74, 90, 138, 0.3)', backdropFilter: 'blur(12px)' }}>
        <CardHeader>
          <CardTitle className="flex items-center justify-between" style={{ color: '#FFD700' }}>
            <div className="flex items-center">
              <Mail className="mr-2 h-5 w-5" />
              Email History
            </div>
            <div className="flex gap-2">
              <Button
                size="sm"
                onClick={() => { fetchEmailLogs(); fetchEmailLogsStats(); }}
                disabled={loading}
                style={{ background: 'rgba(74, 90, 138, 0.5)', color: '#E8E8E8' }}
              >
                <RefreshCw className={`h-4 w-4 mr-1 ${loading ? 'animate-spin' : ''}`} />
                Refresh
              </Button>
              {emailLogs.length > 0 && (
                <Button
                  size="sm"
                  variant="destructive"
                  onClick={handleClearEmailLogs}
                  style={{ background: '#EF4444', color: '#FFFFFF' }}
                >
                  <Trash2 className="h-4 w-4 mr-1" />
                  Clear All
                </Button>
              )}
            </div>
          </CardTitle>
        </CardHeader>
        <CardContent>
          {/* Stats Summary */}
          <div className="grid grid-cols-3 gap-4 mb-4">
            <div className="p-3 rounded" style={{ background: 'rgba(255, 215, 0, 0.1)', border: '1px solid rgba(255, 215, 0, 0.3)' }}>
              <p style={{ color: '#B0B0B0', fontSize: '12px' }}>Total Emails</p>
              <p style={{ color: '#FFD700', fontSize: '24px', fontWeight: 'bold' }}>{emailLogsStats.total}</p>
            </div>
            <div className="p-3 rounded" style={{ background: 'rgba(34, 197, 94, 0.1)', border: '1px solid rgba(34, 197, 94, 0.3)' }}>
              <p style={{ color: '#B0B0B0', fontSize: '12px' }}>Delivered</p>
              <p style={{ color: '#22C55E', fontSize: '24px', fontWeight: 'bold' }}>{emailLogsStats.delivered}</p>
            </div>
            <div className="p-3 rounded" style={{ background: 'rgba(239, 68, 68, 0.1)', border: '1px solid rgba(239, 68, 68, 0.3)' }}>
              <p style={{ color: '#B0B0B0', fontSize: '12px' }}>Failed</p>
              <p style={{ color: '#EF4444', fontSize: '24px', fontWeight: 'bold' }}>{emailLogsStats.failed}</p>
            </div>
          </div>

          {/* Filters */}
          <div className="flex gap-4 mb-4">
            <div className="flex-1">
              <Label style={{ color: '#E8E8E8', fontSize: '12px' }}>Status</Label>
              <Select 
                value={filter.status || 'all'} 
                onValueChange={(value) => setFilter(prev => ({ ...prev, status: value === 'all' ? '' : value }))}
              >
                <SelectTrigger style={{ background: '#0A1128', borderColor: '#4A5A8A', color: '#E8E8E8' }}>
                  <SelectValue placeholder="All Statuses" />
                </SelectTrigger>
                <SelectContent style={{ background: '#1E2A4A', borderColor: '#4A5A8A' }}>
                  <SelectItem value="all" style={{ color: '#E8E8E8' }}>All Statuses</SelectItem>
                  <SelectItem value="delivered" style={{ color: '#22C55E' }}>Delivered</SelectItem>
                  <SelectItem value="failed" style={{ color: '#EF4444' }}>Failed</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="flex-1">
              <Label style={{ color: '#E8E8E8', fontSize: '12px' }}>Type</Label>
              <Select 
                value={filter.email_type || 'all'} 
                onValueChange={(value) => setFilter(prev => ({ ...prev, email_type: value === 'all' ? '' : value }))}
              >
                <SelectTrigger style={{ background: '#0A1128', borderColor: '#4A5A8A', color: '#E8E8E8' }}>
                  <SelectValue placeholder="All Types" />
                </SelectTrigger>
                <SelectContent style={{ background: '#1E2A4A', borderColor: '#4A5A8A' }}>
                  <SelectItem value="all" style={{ color: '#E8E8E8' }}>All Types</SelectItem>
                  <SelectItem value="invoice" style={{ color: '#E8E8E8' }}>Invoice</SelectItem>
                  <SelectItem value="flight_creation" style={{ color: '#E8E8E8' }}>Flight Creation</SelectItem>
                  <SelectItem value="price_list" style={{ color: '#E8E8E8' }}>Price List</SelectItem>
                  <SelectItem value="notification" style={{ color: '#E8E8E8' }}>Notification</SelectItem>
                  <SelectItem value="statement" style={{ color: '#E8E8E8' }}>Statement</SelectItem>
                  <SelectItem value="report" style={{ color: '#E8E8E8' }}>Report</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>

          {/* Logs Table */}
          <div style={{ 
            maxHeight: '500px', 
            overflowY: 'auto',
            border: '1px solid #4A5A8A',
            borderRadius: '4px',
            background: '#0A1128'
          }}>
            {loading ? (
              <p style={{ color: '#B0B0B0', textAlign: 'center', padding: '40px' }}>
                Loading email logs...
              </p>
            ) : emailLogs.length === 0 ? (
              <p style={{ color: '#B0B0B0', textAlign: 'center', padding: '40px' }}>
                No email logs found
              </p>
            ) : (
              <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                <thead>
                  <tr style={{ background: 'rgba(74, 90, 138, 0.3)', position: 'sticky', top: 0 }}>
                    <th style={{ padding: '12px', textAlign: 'left', color: '#FFD700', fontSize: '12px', fontWeight: '600' }}>Status</th>
                    <th style={{ padding: '12px', textAlign: 'left', color: '#FFD700', fontSize: '12px', fontWeight: '600' }}>Type</th>
                    <th style={{ padding: '12px', textAlign: 'left', color: '#FFD700', fontSize: '12px', fontWeight: '600' }}>Recipient / Agency</th>
                    <th style={{ padding: '12px', textAlign: 'left', color: '#FFD700', fontSize: '12px', fontWeight: '600' }}>Related To</th>
                    <th style={{ padding: '12px', textAlign: 'left', color: '#FFD700', fontSize: '12px', fontWeight: '600' }}>Sent By</th>
                    <th style={{ padding: '12px', textAlign: 'left', color: '#FFD700', fontSize: '12px', fontWeight: '600' }}>Date & Time</th>
                  </tr>
                </thead>
                <tbody>
                  {emailLogs.map((log, index) => (
                    <tr 
                      key={log.id || index}
                      style={{ 
                        borderBottom: '1px solid rgba(74, 90, 138, 0.3)',
                        background: index % 2 === 0 ? 'transparent' : 'rgba(74, 90, 138, 0.1)'
                      }}
                    >
                      <td style={{ padding: '12px' }}>
                        {log.status === 'delivered' ? (
                          <span style={{ color: '#22C55E', display: 'flex', alignItems: 'center', gap: '4px' }}>
                            <CheckCircle className="h-4 w-4" /> Delivered
                          </span>
                        ) : (
                          <span style={{ color: '#EF4444', display: 'flex', alignItems: 'center', gap: '4px' }}>
                            <XCircle className="h-4 w-4" /> Failed
                          </span>
                        )}
                      </td>
                      <td style={{ padding: '12px', color: '#E8E8E8', fontSize: '13px' }}>
                        {formatEmailType(log.email_type)}
                      </td>
                      <td style={{ padding: '12px' }}>
                        <div style={{ color: '#FFFFFF', fontSize: '13px' }}>{log.agency_name || log.recipient_name || '-'}</div>
                        <div style={{ color: '#B0B0B0', fontSize: '11px' }}>{log.recipient_email}</div>
                      </td>
                      <td style={{ padding: '12px', color: '#E8E8E8', fontSize: '13px' }}>
                        {log.related_entity_name ? (
                          <span>
                            {log.related_entity_type && <span style={{ color: '#B0B0B0' }}>{log.related_entity_type}: </span>}
                            {log.related_entity_name}
                          </span>
                        ) : '-'}
                      </td>
                      <td style={{ padding: '12px', color: '#B0B0B0', fontSize: '13px' }}>
                        {log.sent_by_name}
                      </td>
                      <td style={{ padding: '12px', color: '#B0B0B0', fontSize: '12px' }}>
                        {new Date(log.sent_at).toLocaleString()}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>

          {/* Error Details for failed emails */}
          {emailLogs.some(log => log.status === 'failed' && log.error_message) && (
            <div className="mt-4 p-3 rounded" style={{ background: 'rgba(239, 68, 68, 0.1)', border: '1px solid rgba(239, 68, 68, 0.3)' }}>
              <p style={{ color: '#EF4444', fontSize: '12px', fontWeight: '500', marginBottom: '8px' }}>
                Recent Errors:
              </p>
              {emailLogs.filter(log => log.status === 'failed' && log.error_message).slice(0, 3).map((log, i) => (
                <p key={i} style={{ color: '#B0B0B0', fontSize: '11px', marginBottom: '4px' }}>
                  • {log.recipient_email}: {log.error_message}
                </p>
              ))}
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export default EmailLogs;
